import { axiosClient } from '@/utils/axios-client'
import { createApiJourneyTemplate, type ApiJourneyTemplate } from '@/model/create-idf-journey.model'
import {
  type JourneyProduct,
  type IdfJourneyTemplate,
  type IdfJourneyTemplateGrondbank,
  type IdfExtraOptions,
} from '@/types/idf-journey-product'
import { defineStore } from 'pinia'
import { ref, type Ref } from 'vue'
import {
  converToApiPayload,
  createEmptyJourneyTemplate,
  createProductsToApiProducts,
  parseEditIdfJourneyTemplate,
} from '@/utils/create-journey'
import type { Product } from '@/types/product'
import type { JourneyTemplate } from '@/types/journey-template'
import type { EditIdfJourney } from '@/model/edit-idf-journey.model'

export const useJourneyTemplateStore = defineStore('journeyStore', () => {
  const journeyTemplate: Ref<IdfJourneyTemplate> = ref<IdfJourneyTemplate>(
    createEmptyJourneyTemplate()
  )
  const journeyTemplateGrondbank: Ref<IdfJourneyTemplateGrondbank | null> =
    ref<IdfJourneyTemplateGrondbank | null>(null)
  const editJourneyTemplate: Ref<EditIdfJourney | null> = ref<EditIdfJourney | null>(null)
  const apiJourneyTemplate = ref<ApiJourneyTemplate>(createApiJourneyTemplate())
  const journeyProducts = ref<JourneyProduct[]>([])
  const canRemoveIhm = ref<boolean>(true)

  function updateJourneyTemplate(collection: Partial<IdfJourneyTemplate>) {
    journeyTemplate.value = {
      ...journeyTemplate.value,
      ...collection,
    }
  }

  async function createIdfJourneyTemplate() {
    convertToPayload(journeyTemplate.value)

    await axiosClient.request<ApiJourneyTemplate>({
      url: '/afvalstroom/api/stroom',
      method: 'POST',
      data: apiJourneyTemplate.value,
    })

    resetApiJourneyTemplate()
  }

  async function editIdfJourneyTemplate() {
    if (editJourneyTemplate.value === null) return

    const journeyId =
      editJourneyTemplate.value?.stroomUUID ?? editJourneyTemplate.value?.stroomUUID_UNIQUE

    convertToPayload(journeyTemplate.value)

    await axiosClient.request<ApiJourneyTemplate>({
      url: `/afvalstroom/api/stroom/${journeyId}/edit`,
      method: 'PUT',
      data: apiJourneyTemplate.value,
    })

    resetApiJourneyTemplate()
    editJourneyTemplate.value = null
    canRemoveIhm.value = true
  }

  function getEditIdfJourneyTemplate(data: EditIdfJourney): IdfJourneyTemplate {
    // reset every time edit is opened
    canRemoveIhm.value = true
    editJourneyTemplate.value = data

    // if stroomIHM undefined if not set in create
    if (data.stroomIHM !== undefined) {
      canRemoveIhm.value = false
    }

    updateJourneyTemplateProduct(data.materialen)
    return parseEditIdfJourneyTemplate(data)
  }

  function convertToPayload(collection: IdfJourneyTemplate) {
    let project

    if (editJourneyTemplate.value !== null) {
      project = { id: editJourneyTemplate.value.project.id }
    } else {
      project = {
        projectUUID: collection.project.projectUUID,
      }
    }

    let extraIdfOptions: IdfExtraOptions | undefined
    let stroomName: string | null

    if (journeyTemplateGrondbank.value !== null) {
      const grondbankData = {
        drieDeligCode: journeyTemplateGrondbank.value.drieDeligCode,
        partijNummer: journeyTemplateGrondbank.value.partijNummer,
        repeating: journeyTemplateGrondbank.value.repeating,
      }
      stroomName = journeyTemplateGrondbank.value.stroomNameGrondbank
      extraIdfOptions = { ...collection.extraOptions, ...grondbankData }
    } else {
      stroomName = collection.stroomName
      extraIdfOptions = collection.extraOptions
    }

    apiJourneyTemplate.value = converToApiPayload(collection, stroomName, project, extraIdfOptions)
  }

  function convertProductsToApiProducts(products: Product[]) {
    const convertedProducts = createProductsToApiProducts(products)
    updateJourneyTemplateProduct(convertedProducts)
  }

  function updateJourneyTemplateProduct(products: JourneyProduct[]) {
    journeyProducts.value = [...products]
  }

  function resetJourneyTemplate() {
    journeyTemplate.value = createEmptyJourneyTemplate()
  }

  function resetApiJourneyTemplate() {
    apiJourneyTemplate.value = createApiJourneyTemplate()
  }

  async function startSharing(journeyTemplate: JourneyTemplate) {
    await axiosClient.request({
      url: `/idf/api/stroom/${journeyTemplate.stroomUUID}/share-with-deva`,
      method: 'POST',
    })
  }

  async function stopSharing(journeyTemplate: JourneyTemplate) {
    await axiosClient.request({
      url: `/idf/api/stroom/${journeyTemplate.stroomUUID}/stop-sharing`,
      method: 'POST',
    })
  }

  function removeIhm() {
    journeyTemplate.value.stroomIhm = null
  }

  type GronbankResponse = {
    codes: string
  }

  async function fetchGrondbankCodes(documentNumber: string) {
    const response = await axiosClient.request<GronbankResponse>({
      url: `/afvalstroom/api/grondbank/code`,
      method: 'GET',
      params: { documentNumber },
    })

    if (journeyTemplateGrondbank.value !== null) {
      journeyTemplateGrondbank.value.drieDeligCode = response.data.codes
    }
  }

  return {
    journeyTemplate,
    journeyTemplateGrondbank,
    journeyProducts,
    updateJourneyTemplateProduct,
    updateJourneyTemplate,
    createIdfJourneyTemplate,
    editIdfJourneyTemplate,
    fetchGrondbankCodes,
    resetJourneyTemplate,
    convertProductsToApiProducts,
    getEditIdfJourneyTemplate,
    startSharing,
    stopSharing,
    removeIhm,
    canRemoveIhm,
  }
})
